import { Box, Tooltip, Typography } from '@mui/material';
import { formatValueAuto } from './fieldSalesFormat.js';

// Reference .panel / .ins styling.
const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';
const SEV = {
  good: '#16A34A',
  warn: '#F59E0B',
  bad: '#DC2626',
  info: '#2E7CF6',
};

const NO_DATA_TOOLTIP = 'No data source yet';
const NO_DATA_TEXT = 'Data not available yet';

/** Small "No data" tag used on every greyed element. */
function NoDataTag() {
  return (
    <Box
      component="span"
      sx={{
        ml: '6px',
        px: '6px',
        py: '1px',
        borderRadius: '999px',
        bgcolor: '#eef1f5',
        border: `1px solid ${BORDER}`,
        color: MUTED,
        fontSize: '9px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.3px',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      No data
    </Box>
  );
}

/**
 * One insight row (reference .ins). When `disabled`, the row is greyed: 50%
 * opacity, not interactive, a "No data source yet" tooltip, a "No data" tag,
 * and the body text replaced with "Data not available yet". `label` is the
 * reference row's lead-in, kept visible so the row stays recognisable.
 */
function Ins({ sev = 'info', icon, disabled = false, label, children }) {
  const row = (
    <Box
      sx={{
        display: 'flex',
        gap: '10px',
        bgcolor: '#fafbfc',
        border: `1px solid ${BORDER}`,
        borderLeft: `4px solid ${disabled ? '#cbd3df' : SEV[sev] ?? SEV.info}`,
        borderRadius: '10px',
        p: '11px 13px',
        ...(disabled
          ? { opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }
          : {}),
      }}
    >
      <Box sx={{ fontSize: '16px', lineHeight: 1.2 }}>{icon}</Box>
      <Typography sx={{ fontSize: '12.7px', color: '#1B2433', lineHeight: 1.5, '& b': { color: NAVY } }}>
        {disabled ? (
          <>
            {label ? <b>{label} </b> : null}
            {NO_DATA_TEXT}
            <NoDataTag />
          </>
        ) : (
          children
        )}
      </Typography>
    </Box>
  );
  // Tooltip must wrap in an interactive-less span (row has pointerEvents:none
  // when disabled), so attach the title to an outer wrapper.
  return disabled ? (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box sx={{ cursor: 'not-allowed' }}>{row}</Box>
    </Tooltip>
  ) : (
    row
  );
}

/**
 * Field Sales — Auto Insights panel. Header "Field Sales - Auto Insights" with
 * "{Mon} · FY26-27 · Value" on the right; a two-column grid that keeps EVERY
 * row from the SalesPulse reference. Rows whose fields exist in summary.insights
 * render live; rows whose source is missing render greyed (never hidden, never
 * invented). The achievement row is computed from card1.
 *
 * @param {{ insights: object, card1?: object, pl: string }} props
 */
export default function FieldSalesInsights({ insights, card1, pl }) {
  const ins = insights ?? {};
  const c1 = card1 ?? {};
  const scope = `${pl} · FY26-27 · Value`;

  const rows = [];

  // 1) Achievement — computed from card1 (sales, target, ach). Live.
  const achPct = Number(c1.ach);
  const sales = Number(c1.sales);
  const target = Number(c1.target);
  if (Number.isFinite(achPct)) {
    const sev = achPct >= 100 ? 'good' : achPct >= 90 ? 'info' : achPct >= 80 ? 'warn' : 'bad';
    const gap = Number.isFinite(target) && Number.isFinite(sales) ? target - sales : NaN;
    rows.push(
      <Ins key="ach" sev={sev} icon={achPct >= 100 ? '🎯' : '⚠️'}>
        {pl} achievement is <b>{achPct.toFixed(1)}%</b> of target (
        {formatValueAuto(sales)} vs {formatValueAuto(target)}).{' '}
        {achPct >= 100
          ? 'Target met.'
          : Number.isFinite(gap)
          ? `Gap of ${formatValueAuto(gap)} to target.`
          : ''}
      </Ins>
    );
  }

  // 2) YoY growth + biggest gainer. The gainer's GROWTH % is not in the API
  // (only brand + value_change), so this row is greyed per spec.
  rows.push(
    <Ins key="gainer" sev="good" icon="📈" disabled label="YoY growth & biggest gainer:" />
  );

  // 3) Biggest drag — drag's GROWTH % is not in the API, so greyed per spec.
  rows.push(
    <Ins key="drag" sev="warn" icon="🔻" disabled label="Biggest drag on growth:" />
  );

  // 4) Top-5 brands. Live when present.
  if (ins.top5_brand_share_pct != null) {
    const v = Number(ins.top5_brand_share_pct);
    rows.push(
      <Ins key="top5" sev={v > 60 ? 'warn' : 'info'} icon="🧩">
        Top 5 brands drive <b>{v.toFixed(0)}%</b> of {pl} sales.{' '}
        {v > 60
          ? 'High concentration — revenue is exposed to a few brands.'
          : 'Reasonably diversified portfolio.'}
      </Ins>
    );
  } else {
    rows.push(<Ins key="top5" sev="info" icon="🧩" disabled label="Top 5 brands:" />);
  }

  // 5) Power brands. Live when present.
  if (ins.power_brand_share_pct != null) {
    rows.push(
      <Ins key="power" sev="info" icon="⭐">
        Power brands contribute <b>{Number(ins.power_brand_share_pct).toFixed(0)}%</b> of {pl} sales.
      </Ins>
    );
  } else {
    rows.push(<Ins key="power" sev="info" icon="⭐" disabled label="Power brands:" />);
  }

  // 6) Discontinued brands — no field in the API, greyed per spec.
  rows.push(
    <Ins key="disc" sev="warn" icon="🗃️" disabled label="Discontinued brands:" />
  );

  // 7) Productivity spread (top vs bottom decile PCPM/TM). Live when present.
  if (ins.top_decile_pcpm_tm != null || ins.bottom_decile_pcpm_tm != null) {
    const top = Number(ins.top_decile_pcpm_tm);
    const bot = Number(ins.bottom_decile_pcpm_tm);
    const ratio = bot > 0 ? top / bot : NaN;
    rows.push(
      <Ins key="spread" sev={Number.isFinite(ratio) && ratio > 5 ? 'warn' : 'info'} icon="⚖️">
        Productivity spread: top-decile HQs average <b>{formatValueAuto(ins.top_decile_pcpm_tm)}</b> PCPM/TM
        vs <b>{formatValueAuto(ins.bottom_decile_pcpm_tm)}</b> for the bottom decile
        {Number.isFinite(ratio) ? (
          <>
            {' '}
            (<b>{ratio.toFixed(1)}×</b> gap)
          </>
        ) : null}
        .{' '}
        {Number.isFinite(ratio) && ratio > 5
          ? 'Wide gap — coaching the bottom cohort is the fastest lever.'
          : 'Relatively even field productivity.'}
      </Ins>
    );
  } else {
    rows.push(<Ins key="spread" sev="info" icon="⚖️" disabled label="Productivity spread:" />);
  }

  // 8) HQs below 90% / no target. The API returns only a single combined count,
  // not the reference's M (total), % and K (no-target) parts, so greyed per spec.
  rows.push(
    <Ins key="below90" sev="info" icon="📍" disabled label="HQs below 90% of target:" />
  );

  // 9) Zone leader & laggard. Live when present.
  const best = ins.best_zone_ach;
  const worst = ins.worst_zone_ach;
  if ((best && best.zone != null) || (worst && worst.zone != null)) {
    rows.push(
      <Ins key="zones" sev="info" icon="🗺️">
        {best && best.zone != null ? (
          <>
            Zone leader: <b>{best.zone}</b> at {Number(best.ach).toFixed(0)}% of target
          </>
        ) : null}
        {best && best.zone != null && worst && worst.zone != null ? '; ' : ''}
        {worst && worst.zone != null ? (
          <>
            laggard: <b>{worst.zone}</b> at {Number(worst.ach).toFixed(0)}%
          </>
        ) : null}
        .
      </Ins>
    );
  } else {
    rows.push(<Ins key="zones" sev="info" icon="🗺️" disabled label="Zone leader & laggard:" />);
  }

  // 10) Divisions span row — greyed per spec (the reference sentence is about
  // the uploaded file; no equivalent data source here).
  rows.push(<Ins key="divs" sev="info" icon="🏢" disabled label="Divisions span:" />);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mb: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          mb: '12px',
        }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>
          💡 Field Sales - Auto Insights
        </Typography>
        <Typography sx={{ fontSize: '12px', color: MUTED }}>{scope}</Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' },
          gap: '12px',
        }}
      >
        {rows}
      </Box>
    </Box>
  );
}
